import { computed, signal, type Signal } from '@angular/core';
import { defer, finalize, type Observable } from 'rxjs';

export interface KeyedLoader {
  isInProgress: (key: string | null) => boolean;
  selectInProgress: (key: string | null) => Signal<boolean>;
  trackInProgress: <T>(
    key: string | null
  ) => (source$: Observable<T>) => Observable<T>;
}

export function createKeyedLoader(): KeyedLoader {
  const keysInProgress = signal<Set<string>>(new Set());

  const isInProgress = (key: string | null): boolean =>
    !!(key && keysInProgress().has(key));

  const selectInProgress = (key: string | null): Signal<boolean> =>
    computed(() => !!(key && keysInProgress().has(key)));

  const setInProgress = (key: string, on: boolean) => {
    keysInProgress.update((current) => {
      const next = new Set(current);
      on ? next.add(key) : next.delete(key);
      return next;
    });
  };

  const trackInProgress =
    <T>(key: string | null) =>
    (source$: Observable<T>) =>
      key
        ? defer(() => {
            setInProgress(key, true);
            return source$.pipe(finalize(() => setInProgress(key, false)));
          })
        : source$;

  return { isInProgress, selectInProgress, trackInProgress };
}
